import { Add, HelpOutline } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { getStoredUser } from '../../../services/authSession';
import { useFetchInfinite, useFetch } from '../../../hooks/useFecth';
import { useSearchTextInput } from '../../../hooks/useSearchTextInput';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';
import AdvertiserUserAssignmentForm from '../components/AdvertiserUserAssignmentForm';
import { ADVERTISER_ROLE_GUIDES, ADVERTISER_ROLE_LABELS, canManageAdvertiserUsers } from '../utils/advertiserPermissions';

const ROLE_LABELS = ADVERTISER_ROLE_LABELS;

function UsersHelpDialog({ open, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Convites e cargos</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            Como funciona o convite
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            Ao convidar, a pessoa recebe um e-mail com link para aceitar. Se ainda não tiver conta, cria a
                            senha nesse link; se já tiver, confirma com a senha atual. O acesso só é liberado depois do
                            aceite.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mt: 1 }}>
                            Se o e-mail não chegar (ou cair no spam), abra a lista de <strong>Convites pendentes</strong>{' '}
                            e use <strong>Copiar link</strong> para enviar o mesmo endereço por WhatsApp ou outro canal.
                            Também dá para <strong>Reenviar</strong> o e-mail ou <strong>Revogar</strong> o convite
                            enquanto estiver pendente.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mt: 1 }}>
                            Apenas <strong>Proprietário</strong> e <strong>Administrador</strong> podem convidar ou
                            gerenciar usuários.
                        </Typography>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            Função de cada cargo
                        </Typography>
                        <List dense disablePadding>
                            {ADVERTISER_ROLE_GUIDES.map(item => (
                                <ListItem key={item.role} alignItems="flex-start" sx={{ px: 0 }}>
                                    <ListItemText
                                        primary={item.label}
                                        secondary={item.description}
                                        primaryTypographyProps={{ fontWeight: 700 }}
                                        secondaryTypographyProps={{ sx: { lineHeight: 1.5 } }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Entendi
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function AdvertiserUsers() {
    const [{ user }] = useCookies(['user']);
    const canManageUsers = canManageAdvertiserUsers(user || getStoredUser());
    const { search, input, handleChange, handleReset } = useSearchTextInput();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const perPage = 10;

    const params = useMemo(
        () => ({
            params: {
                perPage,
                search: search || undefined,
            },
        }),
        [perPage, search],
    );

    const { data: users, isLoading, hasMore, loadMore, mutate, setPage } = useFetchInfinite('/advertiser/users', params);
    const { data: invites, mutate: mutateInvites } = useFetch(canManageUsers ? '/advertiser/invites' : null);
    const { enqueueSnackbar, setErrors } = useErrors();
    const pendingInvites = Array.isArray(invites) ? invites : [];

    useEffect(() => {
        setPage(1);
    }, [search, setPage]);

    useEffect(() => {
        if (!canManageUsers) {
            setDialogOpen(false);
            setEditing(null);
        }
    }, [canManageUsers]);

    const closeDialog = () => {
        setDialogOpen(false);
        setEditing(null);
    };

    const openCreateDialog = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const helpButton = (
        <IconButton
            size="small"
            color="primary"
            aria-label="Instruções sobre convites e cargos"
            onClick={() => setHelpOpen(true)}
        >
            <HelpOutline />
        </IconButton>
    );

    return (
        <Stack spacing={3}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="h4" fontWeight="bold">
                    Usuários do anunciante
                </Typography>
                {helpButton}
            </Stack>

            {!canManageUsers && (
                <Alert severity="info">
                    Apenas proprietário ou administrador podem cadastrar ou editar usuários do anunciante.
                </Alert>
            )}

            <Card>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={canManageUsers ? 8 : 12}>
                            <TextField
                                fullWidth
                                label="Buscar usuário"
                                value={input}
                                onChange={event => handleChange(event.target.value)}
                                InputProps={{
                                    endAdornment: input ? (
                                        <Button size="small" onClick={handleReset}>
                                            Limpar
                                        </Button>
                                    ) : null,
                                }}
                            />
                        </Grid>
                        {canManageUsers && (
                            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                                <Button
                                    variant="contained"
                                    startIcon={<Add />}
                                    onClick={openCreateDialog}
                                    fullWidth
                                    sx={{ maxWidth: { md: 300 } }}
                                >
                                    Convidar usuário
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Usuário</TableCell>
                                <TableCell>Permissão</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users?.map(item => (
                                <TableRow
                                    key={item.id}
                                    hover={canManageUsers}
                                    sx={canManageUsers ? { cursor: 'pointer' } : undefined}
                                    onClick={
                                        canManageUsers
                                            ? () => {
                                                  setEditing(item);
                                                  setDialogOpen(true);
                                              }
                                            : undefined
                                    }
                                >
                                    <TableCell>{item.user?.userName}</TableCell>
                                    <TableCell>{ROLE_LABELS[item.role] || item.role}</TableCell>
                                    <TableCell>{item.status ? 'Ativo' : 'Inativo'}</TableCell>
                                    <TableCell align="right">
                                        {item.user?.createdAt ? new Date(item.user.createdAt).toLocaleDateString() : '--'}
                                        {canManageUsers && item.role !== 'owner' ? (
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={async event => {
                                                    event.stopPropagation();
                                                    if (!window.confirm(`Remover o acesso de ${item.user?.userName || 'este usuário'}?`)) return;
                                                    try {
                                                        await server.delete(`/advertiser/users/${item.id}`);
                                                        enqueueSnackbar('Usuário desvinculado.');
                                                        mutate();
                                                    } catch (error) {
                                                        setErrors(error);
                                                    }
                                                }}
                                            >
                                                Remover
                                            </Button>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {users?.length === 0 && !isLoading && (
                        <Typography variant="body2" color="text.secondary" mt={2}>
                            Nenhum usuário cadastrado.
                        </Typography>
                    )}
                    {hasMore && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Button variant="outlined" onClick={loadMore}>
                                Carregar mais
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {canManageUsers && (
                <Card>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                            <Typography variant="h6">Convites pendentes</Typography>
                            {helpButton}
                        </Stack>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>E-mail</TableCell>
                                    <TableCell>Permissão</TableCell>
                                    <TableCell>Expira em</TableCell>
                                    <TableCell align="right">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pendingInvites.map(invite => (
                                    <TableRow key={invite.id}>
                                        <TableCell>{invite.email}</TableCell>
                                        <TableCell>{ROLE_LABELS[invite.role] || invite.role}</TableCell>
                                        <TableCell>
                                            {invite.expiresAt ? new Date(invite.expiresAt).toLocaleString('pt-BR') : '--'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                onClick={async () => {
                                                    try {
                                                        await navigator.clipboard.writeText(invite.inviteUrl || '');
                                                        enqueueSnackbar('Link do convite copiado.');
                                                    } catch (error) {
                                                        setErrors(error);
                                                    }
                                                }}
                                            >
                                                Copiar link
                                            </Button>
                                            <Button
                                                size="small"
                                                onClick={async () => {
                                                    try {
                                                        await server.post(`/advertiser/invites/${invite.id}/resend`);
                                                        enqueueSnackbar('Convite reenviado.');
                                                        mutateInvites();
                                                    } catch (error) {
                                                        setErrors(error);
                                                    }
                                                }}
                                            >
                                                Reenviar
                                            </Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={async () => {
                                                    try {
                                                        await server.delete(`/advertiser/invites/${invite.id}`);
                                                        enqueueSnackbar('Convite revogado.');
                                                        mutateInvites();
                                                    } catch (error) {
                                                        setErrors(error);
                                                    }
                                                }}
                                            >
                                                Revogar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {pendingInvites.length === 0 && (
                            <Typography variant="body2" color="text.secondary" mt={2}>
                                Nenhum convite pendente.
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}

            {canManageUsers && (
                <Fab color="primary" sx={{ position: 'fixed', bottom: 32, right: 32 }} onClick={openCreateDialog} aria-label="Cadastrar usuário">
                    <Add />
                </Fab>
            )}

            {canManageUsers && (
                <AdvertiserUserAssignmentForm
                    open={dialogOpen}
                    onClose={closeDialog}
                    assignment={editing}
                    onSuccess={async () => {
                        await mutate();
                        await mutateInvites();
                    }}
                />
            )}

            <UsersHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
        </Stack>
    );
}
